/**
 * Workflow parameter vocabulary — the data types a workflow value may carry, the
 * UI-only types laid over them for display, and the recursive `ParameterSetup`
 * shape a node stores its configured inputs in.
 *
 * This module imports nothing from the rest of the workflow family, which is what
 * lets `schema-type.ts` depend on it without the two forming a cycle.
 */

import z from "zod";

/**
 * Parameter types for workflow data - USE FOR ALL DATA OPERATIONS.
 *
 * These represent actual JavaScript/JSON data types. Use this enum whenever
 * you're working with real data that will be stored, processed, or validated.
 *
 * Use this enum in (both frontend & backend):
 * - Type inference functions (inferOutputFormat)
 * - Data validation and serialization
 * - Schema definitions (DataSchemaType, outputFormat)
 * - Workflow execution logic
 * - Any code that handles actual data values
 *
 * Does NOT include UI-only types:
 * - INTEGER (use NUMBER - JS has no native integer type)
 * - UNKNOWN (UI fallback only - use WorkflowDisplayType)
 * - BRANCH (UI concept only - use WorkflowDisplayType)
 *
 * @see {@link WorkflowDisplayType} for UI rendering (icons, fallbacks)
 * @see {@link OpenAPIParameterType} for external OpenAPI/JSON Schema types
 */
export enum WorkflowParameterType {
  STRING = "string",
  NUMBER = "number",
  BOOLEAN = "boolean",
  OBJECT = "object",
  ARRAY = "array",
  NULL = "null"
}

/**
 * UI-only parameter types - NEVER persisted or sent to backend.
 * These are visual placeholders used only in frontend components.
 */
export enum WorkflowUIOnlyType {
  /** Shows branch/decision icons in the variable selector */
  BRANCH = "branch",
  /** Fallback when outputFormat is not yet configured on a node */
  UNKNOWN = "unknown",
  /** Shows variable icons in the variable selector */
  VARIABLE = "variable"
}

/**
 * Extended types for UI display - FRONTEND ONLY.
 *
 * Combines WorkflowParameterType (real data types) with UI-only types
 * (BRANCH, UNKNOWN) for rendering purposes.
 *
 * Use this enum in:
 * - Frontend components rendering type icons
 * - Variable selectors and mention lists
 * - Any UI that needs to display schema types
 *
 * NEVER use in:
 * - Backend logic
 * - Data persistence
 * - Type inference functions
 *
 * Usage:
 * - Type annotation: `type: WorkflowDisplayType`
 * - Value access: `WorkflowDisplayType.STRING`, `WorkflowDisplayType.BRANCH`
 *
 * @see {@link WorkflowParameterType} for backend/data types
 */
export const WorkflowDisplayType = {
  ...WorkflowParameterType,
  ...WorkflowUIOnlyType
} as const;

export type WorkflowDisplayTypeValue =
  (typeof WorkflowDisplayType)[keyof typeof WorkflowDisplayType];

export const workflowParameterTypeSchema = z.preprocess(
  (val) => (val === "integer" ? "number" : val),
  z.nativeEnum(WorkflowParameterType)
);
export const workflowDisplayTypeSchema = z.nativeEnum(WorkflowDisplayType);

/**
 * Parameter value type - represents the possible values for workflow parameters
 * Using unknown for complex nested structures to avoid circular references
 */
export type ParameterValue = string | number | boolean | null | unknown[] | Record<string, unknown>;

/**
 * Parameter option for select/dropdown controls
 */
interface ParameterOption {
  label: string;
  value: ParameterValue;
}

/**
 * Parameter constraints for validation and control
 */
interface ParameterConstraints {
  allowedOptions?: ParameterOption[];
  allowedValues?: ParameterValue[];
  description?: string;
  remoteOptions?: boolean; // For Pipedream dynamic options
  validation?: string;
}

/**
 * Schema definition for array items in nested parameters
 */
export interface ParameterItemSchema {
  properties?: Record<string, unknown> & Record<string, unknown>;
  type: WorkflowParameterType;
}

export type ParameterSetup = {
  [key: string]: {
    // Cached remote options for display purposes
    _cachedRemoteOptions?: ParameterOption[];
    // Constraints for agent-controlled parameters (when handler is "prompt")
    constraints?: ParameterConstraints;
    handler: "manual" | "prompt" | "variable";
    id: string;
    items?: ParameterSetup[keyof ParameterSetup][];
    itemsSchema?: ParameterItemSchema;
    properties?: ParameterSetup;
    prompt?: string;
    type: WorkflowParameterType;
    value?: ParameterValue;
    variable?: string;
  };
};
